import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';

/**
 * CUALencuesta - Modulo de encuestas curadas.
 *
 * Modelo CURADO:
 *  - Solicitantes envian peticion -> CUALAPP cotiza segun matriz -> pago via CUALpay -> encuesta activa.
 *  - Respondedores ganan Cc segun premio configurado.
 *
 * Tabs: Tarifas (publico) / Solicitar / Mis solicitudes / Activas
 */
const CualencuestaComponent = ({ user }) => {
  const [tab, setTab] = useState('active');
  const [tariffs, setTariffs] = useState(null);
  const [activeSurveys, setActiveSurveys] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  const fetchTariffs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cualencuesta/tariffs`);
      setTariffs(r.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchActive = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/cualencuesta/active`);
      setActiveSurveys(r.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error cargando encuestas activas');
    } finally { setLoading(false); }
  }, []);

  const fetchMine = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/cualencuesta/my-requests`);
      setMyRequests(r.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error cargando mis solicitudes');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTariffs(); }, [fetchTariffs]);
  useEffect(() => {
    if (tab === 'active') fetchActive();
    if (tab === 'mine') fetchMine();
  }, [tab, fetchActive, fetchMine]);

  return (
    <div data-testid="cualencuesta-module" className="space-y-4">
      <header className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">CUALencuesta</h2>
        <p className="text-sm text-gray-600 mt-1">
          Plataforma de encuestas curadas. CUALAPP revisa y cotiza cada solicitud para asegurar calidad.
          Responde encuestas activas y gana CUALcoin.
        </p>
      </header>

      <div className="flex gap-1 border-b border-gray-200" data-testid="cualencuesta-tabs">
        {[
          { id: 'active', label: 'Activas' },
          { id: 'request', label: 'Solicitar encuesta' },
          { id: 'mine', label: 'Mis solicitudes' },
          { id: 'tariffs', label: 'Tarifas' },
        ].map(t => (
          <button key={t.id}
            data-testid={`cualencuesta-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="cualencuesta-error">{error}</div>}
      {msg && <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3" data-testid="cualencuesta-msg">{msg}</div>}

      {tab === 'active' && (
        <ActiveSurveys items={activeSurveys} loading={loading} onAnswered={(text) => { setMsg(text); fetchActive(); }} onError={setError} />
      )}
      {tab === 'request' && (
        <RequestForm tariffs={tariffs} onCreated={(text) => { setMsg(text); setTab('mine'); }} onError={setError} />
      )}
      {tab === 'mine' && (
        <MyRequests items={myRequests} loading={loading} />
      )}
      {tab === 'tariffs' && <TariffsTable tariffs={tariffs} />}
    </div>
  );
};

// ===== Activas =====
const ActiveSurveys = ({ items, loading, onAnswered, onError }) => {
  const [open, setOpen] = useState(null); // survey actual
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/cualencuesta/${open.id}/respond`, { answers });
      onAnswered(r.data.message || `Ganaste ${r.data.reward_cc} Cc`);
      setOpen(null); setAnswers({});
    } catch (e) {
      onError(e.response?.data?.detail || 'Error enviando respuestas');
    } finally { setSubmitting(false); }
  };

  if (loading) return <p className="text-sm text-gray-500">Cargando...</p>;
  if (!items.length) return <p className="text-sm text-gray-500" data-testid="cualencuesta-empty-active">No hay encuestas activas en este momento.</p>;
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="cualencuesta-active-list">
      {items.map(s => (
        <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900">{s.title}</h3>
            <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-xs font-medium">{s.quote?.category || ''}</span>
          </div>
          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{s.description}</p>
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-emerald-700 font-semibold">+ {s.reward_per_response_cc} Cc</span>
            <span className="text-gray-500">{s.responses_count || 0} / {s.target_responses}</span>
          </div>
          <button
            data-testid={`cualencuesta-answer-${s.id}`}
            disabled={s.already_responded}
            onClick={() => setOpen(s)}
            className={`mt-3 w-full px-3 py-2 rounded-lg text-sm font-semibold ${
              s.already_responded
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}>
            {s.already_responded ? 'Ya respondiste' : 'Responder'}
          </button>
        </div>
      ))}

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cualencuesta-answer-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b">
              <h3 className="text-lg font-bold">{open.title}</h3>
              <p className="text-xs text-gray-600 mt-1">{open.description}</p>
              <p className="text-xs text-emerald-700 mt-2">Premio: +{open.reward_per_response_cc} Cc</p>
            </div>
            <div className="p-5 space-y-4">
              {(open.questions || []).map((q, idx) => (
                <QuestionInput key={q.id} idx={idx} q={q} value={answers[q.id]} onChange={(v) => setAnswers(a => ({ ...a, [q.id]: v }))} />
              ))}
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <button onClick={() => { setOpen(null); setAnswers({}); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancelar</button>
              <button data-testid="cualencuesta-submit-answers" onClick={submit} disabled={submitting}
                className="px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50">
                {submitting ? 'Enviando...' : 'Enviar respuestas'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionInput = ({ q, idx, value, onChange }) => {
  const label = <label className="block text-sm font-medium text-gray-800 mb-2">{idx + 1}. {q.text}{q.required && <span className="text-red-500"> *</span>}</label>;
  if (q.type === 'single' || q.type === 'yes_no') {
    const opts = q.type === 'yes_no' ? ['Si', 'No'] : (q.options || []);
    return (
      <div>{label}
        <div className="space-y-1">
          {opts.map(o => (
            <label key={o} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="radio" name={q.id} value={o} checked={value === o} onChange={(e) => onChange(e.target.value)} />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === 'multiple') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>{label}
        <div className="space-y-1">
          {(q.options || []).map(o => (
            <label key={o} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={arr.includes(o)} onChange={(e) => onChange(e.target.checked ? [...arr, o] : arr.filter(x => x !== o))} />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === 'rating') {
    return (
      <div>{label}
        <div className="flex gap-1">
          {[1,2,3,4,5].map(n => (
            <button key={n} type="button" onClick={() => onChange(n)}
              className={`w-9 h-9 rounded ${value === n ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {n}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div>{label}
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" maxLength={1000} />
    </div>
  );
};

// ===== Solicitar =====
const RequestForm = ({ tariffs, onCreated, onError }) => {
  const [form, setForm] = useState({
    title: '', description: '', purpose: '', target_responses: 100,
    visibility: 'private', requested_category: 'L2',
  });
  const [questions, setQuestions] = useState([
    { id: 'q1', type: 'single', text: '', options: ['', ''], required: true },
  ]);
  const [audience, setAudience] = useState({ countries: '', celula_tiers: '' });
  const [submitting, setSubmitting] = useState(false);

  const addQ = () => setQuestions(qs => [...qs, { id: `q${qs.length + 1}`, type: 'single', text: '', options: ['', ''], required: true }]);
  const updateQ = (i, patch) => setQuestions(qs => qs.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const removeQ = (i) => setQuestions(qs => qs.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.purpose.trim()) {
      onError('Completa titulo, descripcion y proposito.');
      return;
    }
    if (!questions.length || questions.some(q => !q.text.trim())) {
      onError('Todas las preguntas deben tener texto.');
      return;
    }
    setSubmitting(true);
    try {
      const cleanedQuestions = questions.map(q => ({
        id: q.id,
        type: q.type,
        text: q.text,
        required: q.required,
        options: ['single', 'multiple'].includes(q.type) ? (q.options || []).filter(o => o.trim()) : null,
      }));
      const payload = {
        title: form.title,
        description: form.description,
        purpose: form.purpose,
        questions: cleanedQuestions,
        target_responses: parseInt(form.target_responses, 10),
        visibility: form.visibility,
        requested_category: form.requested_category,
        audience: {
          countries: audience.countries ? audience.countries.split(',').map(s => s.trim()).filter(Boolean) : null,
          celula_tiers: audience.celula_tiers ? audience.celula_tiers.split(',').map(s => s.trim()).filter(Boolean) : null,
        },
      };
      const r = await axios.post(`${API}/cualencuesta/request`, payload);
      onCreated(r.data.message || 'Solicitud enviada');
      setForm({ title: '', description: '', purpose: '', target_responses: 100, visibility: 'private', requested_category: 'L2' });
      setQuestions([{ id: 'q1', type: 'single', text: '', options: ['', ''], required: true }]);
    } catch (e) {
      onError(e.response?.data?.detail || 'Error creando solicitud');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4 bg-white rounded-xl border border-gray-200 p-5" data-testid="cualencuesta-request-form">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
        Tu solicitud sera revisada por CUALAPP. Te enviaremos una cotizacion ajustada segun la matriz oficial.
      </div>

      <Input label="Titulo" testid="req-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} max={200} />
      <Textarea label="Descripcion" testid="req-description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} max={2000} />
      <Textarea label="Proposito (para que se usaran los resultados)" testid="req-purpose" value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v })} max={1000} />

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Respuestas objetivo</label>
          <input data-testid="req-target" type="number" min={10} max={100000} value={form.target_responses}
            onChange={(e) => setForm({ ...form, target_responses: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Visibilidad de resultados</label>
          <select data-testid="req-visibility" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="private">Privado (solo solicitante)</option>
            <option value="respondents">Respondedores</option>
            <option value="public">Publico</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Categoria sugerida</label>
          <select data-testid="req-category" value={form.requested_category} onChange={(e) => setForm({ ...form, requested_category: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
            {tariffs && Object.entries(tariffs.categories || {}).map(([k, v]) => (
              <option key={k} value={k}>{k} - {v.name} (${v.price_usd})</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Audiencia: paises (codigo ISO, separa por coma)</label>
          <input data-testid="req-countries" value={audience.countries} onChange={(e) => setAudience({ ...audience, countries: e.target.value })}
            placeholder="VE, CO, MX" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Audiencia: tiers de Celula (semilla, basico, etc.)</label>
          <input data-testid="req-tiers" value={audience.celula_tiers} onChange={(e) => setAudience({ ...audience, celula_tiers: e.target.value })}
            placeholder="semilla, basico" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-800">Preguntas</h4>
          <button data-testid="req-add-question" onClick={addQ} className="text-xs text-purple-700 hover:underline">+ Agregar pregunta</button>
        </div>
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500">#{i + 1}</span>
                <select value={q.type} onChange={(e) => updateQ(i, { type: e.target.value })}
                  data-testid={`req-q-type-${i}`}
                  className="border border-gray-300 rounded px-2 py-1 text-xs">
                  <option value="single">Una opcion</option>
                  <option value="multiple">Multiple</option>
                  <option value="yes_no">Si/No</option>
                  <option value="rating">Escala 1-5</option>
                  <option value="text">Texto libre</option>
                </select>
                {questions.length > 1 && (
                  <button onClick={() => removeQ(i)} className="ml-auto text-xs text-red-600 hover:underline">Eliminar</button>
                )}
              </div>
              <input data-testid={`req-q-text-${i}`} value={q.text} onChange={(e) => updateQ(i, { text: e.target.value })}
                placeholder="Texto de la pregunta" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" maxLength={500} />
              {['single', 'multiple'].includes(q.type) && (
                <div className="space-y-1">
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} className="flex gap-2">
                      <input value={opt} onChange={(e) => {
                        const next = [...(q.options || [])]; next[oi] = e.target.value; updateQ(i, { options: next });
                      }} placeholder={`Opcion ${oi + 1}`} className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs" />
                      {q.options.length > 2 && (
                        <button onClick={() => updateQ(i, { options: q.options.filter((_, x) => x !== oi) })} className="text-xs text-red-500">x</button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => updateQ(i, { options: [...(q.options || []), ''] })} className="text-xs text-purple-700 hover:underline">+ Opcion</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <button data-testid="req-submit" onClick={submit} disabled={submitting}
        className="w-full px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50">
        {submitting ? 'Enviando...' : 'Enviar solicitud para revision'}
      </button>
    </div>
  );
};

const Input = ({ label, value, onChange, max, testid }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} maxLength={max}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
  </div>
);
const Textarea = ({ label, value, onChange, max, testid }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} maxLength={max} rows={3}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
  </div>
);

// ===== Mis solicitudes =====
const STATUS_BADGE = {
  pending_review: { label: 'En revision', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  quoted: { label: 'Cotizada', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  awaiting_payment: { label: 'Esperando pago', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  active: { label: 'Activa', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed: { label: 'Cerrada', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  rejected: { label: 'Rechazada', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const MyRequests = ({ items, loading }) => {
  if (loading) return <p className="text-sm text-gray-500">Cargando...</p>;
  if (!items.length) return <p className="text-sm text-gray-500" data-testid="cualencuesta-empty-mine">Aun no has enviado solicitudes.</p>;
  return (
    <div className="space-y-3" data-testid="cualencuesta-mine-list">
      {items.map(s => {
        const badge = STATUS_BADGE[s.status] || { label: s.status, cls: 'bg-gray-50 text-gray-700 border-gray-200' };
        return (
          <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-900">{s.title}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{new Date(s.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${badge.cls}`}>{badge.label}</span>
            </div>
            {s.quote && (
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div className="bg-gray-50 rounded p-2"><p className="text-gray-500">Categoria</p><p className="font-semibold">{s.quote.category} - {s.quote.category_name}</p></div>
                <div className="bg-gray-50 rounded p-2"><p className="text-gray-500">Precio</p><p className="font-semibold">${s.quote.final_price_usd}</p></div>
                <div className="bg-gray-50 rounded p-2"><p className="text-gray-500">Premio total</p><p className="font-semibold">{s.quote.premium_total_cc} Cc</p></div>
              </div>
            )}
            {s.rejection_reason && <p className="mt-2 text-xs text-red-700">Motivo: {s.rejection_reason}</p>}
            {s.admin_note && <p className="mt-2 text-xs text-gray-600 italic">Nota CUALAPP: {s.admin_note}</p>}
            {s.status === 'quoted' && (
              <p className="mt-3 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded p-2">
                Cotizacion lista. Comunicate con CUALAPP para coordinar el pago via CUALpay y activar tu encuesta.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ===== Tarifas =====
const TariffsTable = ({ tariffs }) => {
  if (!tariffs) return <p className="text-sm text-gray-500">Cargando tarifas...</p>;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="cualencuesta-tariffs">
      <p className="text-xs text-gray-600">Version {tariffs.version} - Pago via {tariffs.payment_via}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr><th className="text-left p-2">Categoria</th><th className="text-right p-2">Precio USD</th><th className="text-right p-2">Premio total Cc</th><th className="text-right p-2">Retencion</th></tr>
          </thead>
          <tbody>
            {Object.entries(tariffs.categories || {}).map(([k, v]) => (
              <tr key={k} className="border-t border-gray-100">
                <td className="p-2"><strong>{k}</strong> - {v.name}</td>
                <td className="p-2 text-right">${v.price_usd}</td>
                <td className="p-2 text-right">{v.premium_cc.toLocaleString('es-VE')}</td>
                <td className="p-2 text-right">{v.retention_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">Modificadores</h4>
        <div className="grid sm:grid-cols-2 gap-2 text-xs">
          {Object.entries(tariffs.modifiers || {}).map(([k, v]) => (
            <div key={k} className="bg-gray-50 rounded p-2 flex justify-between">
              <span className="text-gray-700">{k.replace(/_/g, ' ')}</span>
              <span className="font-semibold text-purple-700">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CualencuestaComponent;
